// Directions page — a guest-friendly index of every Xaluca property with
// addresses, GPS coordinates, step-by-step directions, estimated travel
// times, contact details and direct Google Maps navigation links.
//
// Data is fetched from /api/directions which merges static defaults with
// any CMS overrides. Property names + categories come from the static
// HOTELS registry. Cards render a small interactive Leaflet map per
// property.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, ZoomControl } from "react-leaflet";
import MapTileToggle from "@/components/maps/MapTileToggle";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import RoomImageGallery from "@/components/RoomImageGallery";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { TESTIMONIALS } from "@/lib/data";
import { useTestimonials } from "@/lib/useTestimonials";
import {
  useHotelPropertyGalleries,
  getHeroUrl,
  getGalleryUrls,
} from "@/lib/useHotelPropertyGalleries";

const XALUCA_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin, Navigation2, Mail, Phone, Plane, Clock, Lightbulb,
  Building, Tent, Mountain, ChevronRight, Compass,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { useDirections } from "@/lib/useDirections";
import MoroccoRegionsMap from "@/components/MoroccoRegionsMap";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const CATEGORY_META = {
  hotel:   { label: { en: "Hotel",         fr: "Hôtel",         es: "Hotel" },          color: "#C16542", icon: Building },
  bivouac: { label: { en: "Bivouac",       fr: "Bivouac",       es: "Bivac" },          color: "#2C2621", icon: Tent },
  refuge:  { label: { en: "Mountain Refuge", fr: "Refuge de montagne", es: "Refugio" }, color: "#5C7C4F", icon: Mountain },
};

const buildMarker = (color) =>
  L.divIcon({
    className: "xaluca-direction-marker",
    html: `
      <div style="position:relative;display:flex;align-items:center;justify-content:center;">
        <span style="
          width:30px;height:30px;border-radius:50%;
          background:${color};
          box-shadow:0 4px 10px rgba(28,22,18,0.35),0 0 0 4px rgba(253,251,247,0.9);
          display:flex;align-items:center;justify-content:center;
          color:#FDFBF7;font-weight:600;font-size:14px;font-family:'Outfit',sans-serif;
          transform:translateY(-50%);
        ">▲</span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

// Small interactive map per property — no search, no filter UI, just the
// single marker and pan/zoom controls.
const PropertyMiniMap = ({ coords, color, name }) => {
  if (!coords || coords.length !== 2) return null;
  const icon = buildMarker(color);
  return (
    <div
      className="relative h-full min-h-[280px] md:min-h-[340px] overflow-hidden border border-[#2C2621]/10"
      data-testid="directions-mini-map"
    >
      <MapContainer
        center={coords}
        zoom={13}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", background: "#F3EBDD" }}
      >
        <MapTileToggle
          mapUrl="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          mapAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          mapMaxZoom={18}
        />
        <ZoomControl position="topright" />
        <Marker position={coords} icon={icon} title={name} />
      </MapContainer>
    </div>
  );
};

const FilterPill = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${
      active
        ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
        : "bg-transparent text-[#2C2621] border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542]"
    }`}
  >
    {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={1.6} /> : null}
    {label}
  </button>
);

const DirectionsCard = ({ item, hotel, lang }) => {
  const cat = CATEGORY_META[hotel.category] || CATEGORY_META.hotel;
  const CatIcon = cat.icon;
  const directionsText = pick(item.directions, lang);
  const tipsText = pick(item.travel_tips, lang);
  const airportText = pick(item.airport_transfer, lang);
  const addressText = pick(item.address, lang);
  const hasMapsLink = !!(item.google_maps_url && item.google_maps_url.trim());
  const slug = item.hotel_slug;
  const intro = pick(hotel.intro, lang);
  const regionTagline = pick(
    { en: hotel.region, fr: hotel.region_fr || hotel.region, es: hotel.region_es || hotel.region },
    lang
  );

  // Image gallery — pull from CMS hero/gallery of the property's
  // "about-hotel" section, then fall back to the brand image, the CMS
  // override `thumbnail_url`, and the static gallery. Same priority used
  // by the property cards on the homepage and /hotels listing.
  const sections = useHotelPropertyGalleries(slug);
  const { items: propertyTestimonials } = useTestimonials(slug);
  const galleryImages = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (u) => {
      if (typeof u !== "string" || !u || seen.has(u)) return;
      seen.add(u);
      out.push(u);
    };
    push(getHeroUrl(sections, "about-hotel"));
    push((item.thumbnail_url && item.thumbnail_url.trim()) || "");
    push(hotel.image);
    getGalleryUrls(sections, "about-hotel").forEach(push);
    (hotel.gallery || []).forEach(push);
    return out;
  }, [sections, item.thumbnail_url, hotel.image, hotel.gallery]);

  return (
    <article
      className="bg-white border border-[#2C2621]/10 overflow-hidden flex flex-col"
      data-testid={`directions-card-${slug}`}
      id={`property-${slug}`}
    >
      {/* Hero header — gallery (left, same width as map below) + property info (right) */}
      <header className="grid md:grid-cols-2 border-b border-[#2C2621]/10">
        {/* Featured-image gallery (main + thumbnail slider) */}
        <div
          className="relative bg-[#F3EBDD]"
          data-testid={`directions-card-gallery-${slug}`}
        >
          {galleryImages.length > 0 ? (
            <RoomImageGallery
              images={galleryImages}
              alt={hotel.name}
              aspect="aspect-[16/10] md:aspect-[4/3]"
              testIdPrefix={`directions-card-${slug}-gallery`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#5C5248]">
              <CatIcon className="w-8 h-8" strokeWidth={1.2} />
            </div>
          )}
          {/* Region tagline pinned over the main image (mirrors the previous
              card design so guests instantly see the area). */}
          {regionTagline ? (
            <span
              className="absolute top-4 left-4 z-[2] inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] tracking-[0.28em] uppercase text-[#FDFBF7] bg-[#1A1513]/65 backdrop-blur-sm pointer-events-none"
              data-testid={`directions-card-region-${slug}`}
            >
              {regionTagline}
            </span>
          ) : null}
          {/* Xaluca circular logo pinned top-right of the featured image —
              consistent placement across desktop, tablet and mobile. */}
          <img
            src={XALUCA_LOGO_URL}
            alt=""
            aria-hidden="true"
            data-no-xaluca-logo="true"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[2] w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover ring-1 ring-white/35 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.55)] pointer-events-none"
            data-testid={`directions-card-${slug}-logo`} loading="lazy" decoding="async"/>
        </div>

        {/* Info column */}
        <div className="p-6 md:p-8 flex flex-col">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] tracking-[0.28em] uppercase"
              style={{ background: `${cat.color}14`, color: cat.color }}
              data-testid={`directions-card-category-${slug}`}
            >
              <CatIcon className="w-3 h-3" strokeWidth={1.8} />
              {pick(cat.label, lang)}
            </span>
            <Link
              to={`/hotels/${slug}`}
              className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:gap-2 transition-all whitespace-nowrap"
              data-testid={`directions-card-view-property-${slug}`}
            >
              {pick({ en: "View property", fr: "Voir la propriété", es: "Ver propiedad" }, lang)}
              <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            </Link>
          </div>

          <h3
            className="font-serif-x text-2xl md:text-3xl mt-3 tracking-tight leading-tight text-[#1A1513]"
            data-testid={`directions-card-name-${slug}`}
          >
            {hotel.name}
          </h3>
          <p className="mt-2 text-sm text-[#5C5248]">{hotel.location}</p>

          {intro ? (
            <p className="mt-4 text-sm text-[#2C2621] leading-relaxed line-clamp-3" data-testid={`directions-card-intro-${slug}`}>
              {intro}
            </p>
          ) : null}

          {/* Testimonials slider — CMS-driven, with default fallback. */}
          <div className="mt-5">
            <TestimonialsCarousel
              testimonials={propertyTestimonials.length ? propertyTestimonials : TESTIMONIALS}
              lang={lang}
              testIdPrefix={`directions-card-${slug}-testimonials`}
            />
          </div>

          {/* Mobile-only view-property link (since the desktop one is in the top row) */}
          <Link
            to={`/hotels/${slug}`}
            className="md:hidden mt-5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:gap-2 transition-all whitespace-nowrap self-start"
            data-testid={`directions-card-view-property-mobile-${slug}`}
          >
            {pick({ en: "View property", fr: "Voir la propriété", es: "Ver propiedad" }, lang)}
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
          </Link>
        </div>
      </header>

      {/* Map + main info */}
      <div className="grid md:grid-cols-2">
        <PropertyMiniMap coords={item.coords} color={cat.color} name={hotel.name} />

        <div className="p-6 md:p-8 space-y-5">
          {/* Address */}
          {addressText ? (
            <div className="flex items-start gap-3" data-testid={`directions-card-address-${slug}`}>
              <MapPin className="w-4 h-4 mt-0.5 text-[#C16542] shrink-0" strokeWidth={1.8} />
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C5248]">
                  {pick({ en: "Address", fr: "Adresse", es: "Dirección" }, lang)}
                </p>
                <p className="mt-1 text-sm text-[#2C2621] leading-relaxed">{addressText}</p>
              </div>
            </div>
          ) : null}

          {/* GPS */}
          <div className="flex items-start gap-3">
            <Compass className="w-4 h-4 mt-0.5 text-[#C16542] shrink-0" strokeWidth={1.8} />
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C5248]">
                {pick({ en: "GPS coordinates", fr: "Coordonnées GPS", es: "Coordenadas GPS" }, lang)}
              </p>
              <p
                className="mt-1 text-sm text-[#2C2621] tabular-nums"
                data-testid={`directions-card-coords-${slug}`}
              >
                {item.coords?.[0]?.toFixed(4)}, {item.coords?.[1]?.toFixed(4)}
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 pt-2 border-t border-[#2C2621]/10">
            <a
              href={`mailto:${item.email}?subject=Enquiry%20%E2%80%94%20${encodeURIComponent(hotel.name)}`}
              className="inline-flex items-center gap-2 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
              data-testid={`directions-card-email-${slug}`}
            >
              <Mail className="w-4 h-4" strokeWidth={1.6} />
              <span className="underline-offset-2 hover:underline">{item.email}</span>
            </a>
            {item.phone ? (
              <a
                href={`tel:${item.phone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-2 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
                data-testid={`directions-card-phone-${slug}`}
              >
                <Phone className="w-4 h-4" strokeWidth={1.6} />
                <span className="tabular-nums">{item.phone}</span>
              </a>
            ) : null}
          </div>

          {/* Google Maps CTA — only when URL is set */}
          {hasMapsLink ? (
            <a
              href={item.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-5 py-3 text-[10px] tracking-[0.3em] uppercase transition-colors w-full sm:w-auto"
              data-testid={`directions-card-maps-${slug}`}
            >
              <Navigation2 className="w-3.5 h-3.5" strokeWidth={1.8} />
              {pick({ en: "Open in Google Maps", fr: "Ouvrir dans Google Maps", es: "Abrir en Google Maps" }, lang)}
            </a>
          ) : (
            <p
              className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] italic"
              data-testid={`directions-card-maps-pending-${slug}`}
            >
              {pick(
                {
                  en: "Direct navigation link coming soon — please contact us for tailored directions.",
                  fr: "Lien de navigation directe bientôt disponible — contactez-nous pour un itinéraire sur mesure.",
                  es: "Enlace de navegación directo próximamente — contáctenos para una ruta personalizada.",
                },
                lang
              )}
            </p>
          )}
        </div>
      </div>

      {/* Step-by-step + estimated times + tips */}
      <div className="px-6 md:px-8 pb-8 pt-6 border-t border-[#2C2621]/10 grid lg:grid-cols-2 gap-8 bg-[#FBF6EE]">
        {directionsText ? (
          <div data-testid={`directions-card-directions-${slug}`}>
            <p className="overline text-[#C16542]">
              {pick({ en: "Step-by-step directions", fr: "Itinéraire pas à pas", es: "Indicaciones paso a paso" }, lang)}
            </p>
            <p className="mt-3 text-sm text-[#2C2621] leading-relaxed">{directionsText}</p>
          </div>
        ) : null}

        {item.estimated_times && item.estimated_times.length > 0 ? (
          <div data-testid={`directions-card-times-${slug}`}>
            <p className="overline text-[#C16542] inline-flex items-center gap-2">
              <Clock className="w-3 h-3" strokeWidth={1.8} />
              {pick({ en: "Estimated travel times", fr: "Temps de trajet estimés", es: "Tiempos de viaje estimados" }, lang)}
            </p>
            <ul className="mt-3 space-y-2">
              {item.estimated_times.map((row, i) => {
                const fromLabel = pick(row.from || {}, lang);
                const key = `${slug}-time-${i}-${(fromLabel || "").slice(0, 10)}`;
                return (
                  <li
                    key={key}
                    className="flex items-baseline justify-between gap-4 text-sm text-[#2C2621] border-b border-[#2C2621]/10 last:border-b-0 pb-2"
                  >
                    <span className="flex-1 truncate">{fromLabel}</span>
                    <span className="font-medium tabular-nums whitespace-nowrap">{row.time}</span>
                    {row.distance ? (
                      <span className="text-xs text-[#5C5248] whitespace-nowrap tabular-nums">{row.distance}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {airportText ? (
          <div data-testid={`directions-card-airport-${slug}`}>
            <p className="overline text-[#C16542] inline-flex items-center gap-2">
              <Plane className="w-3 h-3" strokeWidth={1.8} />
              {pick({ en: "Airport transfers", fr: "Transferts aéroport", es: "Traslados aeropuerto" }, lang)}
            </p>
            <p className="mt-3 text-sm text-[#2C2621] leading-relaxed">{airportText}</p>
          </div>
        ) : null}

        {tipsText ? (
          <div data-testid={`directions-card-tips-${slug}`}>
            <p className="overline text-[#C16542] inline-flex items-center gap-2">
              <Lightbulb className="w-3 h-3" strokeWidth={1.8} />
              {pick({ en: "Travel tips", fr: "Conseils pratiques", es: "Consejos prácticos" }, lang)}
            </p>
            <p className="mt-3 text-sm text-[#2C2621] leading-relaxed">{tipsText}</p>
          </div>
        ) : null}
      </div>
    </article>
  );
};

const DirectionsPage = () => {
  const { lang } = useLanguage();
  const { items, loading, error } = useDirections();
  const [filter, setFilter] = useState("all");
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/regions`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        setRegions(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => { /* non-fatal — the rest of the page still works */ });
    return () => { cancelled = true; };
  }, []);

  // Match every backend item with its static HOTELS metadata (name, category).
  const cards = useMemo(() => {
    const bySlug = Object.fromEntries(HOTELS.map((h) => [h.slug, h]));
    return items
      .map((it) => ({ item: it, hotel: bySlug[it.hotel_slug] }))
      .filter(({ hotel }) => !!hotel)
      .filter(({ hotel }) => filter === "all" || hotel.category === filter)
      // Order: hotels → bivouacs → refuges, then alphabetical by name.
      .sort((a, b) => {
        const order = { hotel: 0, bivouac: 1, refuge: 2 };
        const ao = order[a.hotel.category] ?? 9;
        const bo = order[b.hotel.category] ?? 9;
        if (ao !== bo) return ao - bo;
        return a.hotel.name.localeCompare(b.hotel.name);
      });
  }, [items, filter]);

  const heroTitle = pick(
    { en: "Find your way to Xaluca", fr: "Trouvez votre route vers Xaluca", es: "Encuentra tu camino hacia Xaluca" },
    lang
  );
  const heroSub = pick(
    {
      en: "Every kasbah, riad, auberge and desert camp — with maps, GPS coordinates, step-by-step directions and direct Google Maps links.",
      fr: "Chaque kasbah, riad, auberge et bivouac — avec cartes, coordonnées GPS, itinéraires détaillés et liens directs vers Google Maps.",
      es: "Cada kasbah, riad, auberge y campamento — con mapas, coordenadas GPS, indicaciones detalladas y enlaces directos a Google Maps.",
    },
    lang
  );

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="directions-page">
      {/* Hero */}
      <header className="relative isolate overflow-hidden bg-[#1A1513] text-[#FDFBF7]">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "url(https://images.unsplash.com/photo-1547235001-d703406d3b62?auto=format&fit=crop&w=2400&q=80) center/cover",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-[#1A1513]/60 via-[#1A1513]/75 to-[#1A1513]"
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
          <p className="overline text-[#D4A373]">
            {pick({ en: "Directions · Find us", fr: "Itinéraires · Nous trouver", es: "Cómo llegar · Encuéntrenos" }, lang)}
          </p>
          <h1
            className="font-serif-x text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05] max-w-3xl"
            data-testid="directions-hero-title"
          >
            {heroTitle}
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#E9DDC9] max-w-2xl leading-relaxed">
            {heroSub}
          </p>

          {/* Filter pills */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="directions-filters">
            <FilterPill
              active={filter === "all"}
              onClick={() => setFilter("all")}
              label={pick({ en: "All properties", fr: "Toutes les propriétés", es: "Todas las propiedades" }, lang)}
              testid="directions-filter-all"
            />
            <FilterPill
              active={filter === "hotel"}
              onClick={() => setFilter("hotel")}
              icon={Building}
              label={pick({ en: "Hotels & Riads", fr: "Hôtels & Riads", es: "Hoteles & Riads" }, lang)}
              testid="directions-filter-hotel"
            />
            <FilterPill
              active={filter === "bivouac"}
              onClick={() => setFilter("bivouac")}
              icon={Tent}
              label={pick({ en: "Bivouacs & Camps", fr: "Bivouacs & Camps", es: "Vivacs & Campamentos" }, lang)}
              testid="directions-filter-bivouac"
            />
            <FilterPill
              active={filter === "refuge"}
              onClick={() => setFilter("refuge")}
              icon={Mountain}
              label={pick({ en: "Mountain Refuges", fr: "Refuges de montagne", es: "Refugios de montaña" }, lang)}
              testid="directions-filter-refuge"
            />
          </div>
        </div>
        <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="page-hero-brand" />
      </header>

      {/* Xaluca Across Morocco — interactive regional map */}
      <MoroccoRegionsMap
        regions={regions}
        directionsBySlug={Object.fromEntries(items.map((it) => [it.hotel_slug, it]))}
      />

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <header className="mb-10 md:mb-12 max-w-3xl">
          <p className="overline text-[#C16542]">
            {pick(
              { en: "Property directory", fr: "Annuaire des propriétés", es: "Directorio de propiedades" },
              lang
            )}
          </p>
          <h2 className="font-serif-x text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-[1.05] text-[#1A1513]">
            {pick(
              { en: "Every address, mapped", fr: "Chaque adresse, cartographiée", es: "Cada dirección, en el mapa" },
              lang
            )}
          </h2>
          <p className="mt-4 text-base text-[#5C5248] leading-relaxed">
            {pick(
              {
                en: "Filter by category and tap any property for its full address, GPS coordinates, step-by-step directions and a direct Google Maps link.",
                fr: "Filtrez par catégorie et ouvrez chaque propriété pour son adresse complète, ses coordonnées GPS, son itinéraire détaillé et son lien Google Maps direct.",
                es: "Filtre por categoría y abra cada propiedad para ver su dirección completa, coordenadas GPS, indicaciones detalladas y enlace directo a Google Maps.",
              },
              lang
            )}
          </p>
        </header>
        {loading ? (
          <p
            className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]"
            data-testid="directions-loading"
          >
            {pick({ en: "Loading directions…", fr: "Chargement des itinéraires…", es: "Cargando indicaciones…" }, lang)}
          </p>
        ) : null}

        {error ? (
          <p className="text-sm text-[#C16542]" data-testid="directions-error">
            {error}
          </p>
        ) : null}

        {!loading && cards.length === 0 ? (
          <p className="text-sm text-[#5C5248]" data-testid="directions-empty">
            {pick(
              { en: "No properties match this filter.", fr: "Aucune propriété ne correspond.", es: "No hay propiedades en este filtro." },
              lang
            )}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-8 md:gap-10" data-testid="directions-list">
          {cards.map(({ item, hotel }) => (
            <DirectionsCard key={item.hotel_slug} item={item} hotel={hotel} lang={lang} />
          ))}
        </div>

        {/* Footer help */}
        <div className="mt-16 md:mt-20 bg-[#2C2621] text-[#FDFBF7] px-8 md:px-12 py-12 md:py-14">
          <p className="overline text-[#D4A373]">
            {pick({ en: "Need a hand?", fr: "Besoin d'aide ?", es: "¿Necesita ayuda?" }, lang)}
          </p>
          <h2 className="font-serif-x text-3xl md:text-4xl mt-3 tracking-tight max-w-2xl">
            {pick(
              {
                en: "Tell us where you're flying in from — we'll send a precise driving itinerary.",
                fr: "Dites-nous d'où vous arrivez — nous vous enverrons un itinéraire précis.",
                es: "Cuéntenos desde dónde vuela — le enviaremos un itinerario detallado.",
              },
              lang
            )}
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact?tab=inquiry"
              className="inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
              data-testid="directions-footer-contact"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={1.8} />
              {pick({ en: "Contact our team", fr: "Contacter notre équipe", es: "Contactar al equipo" }, lang)}
            </Link>
            <a
              href="mailto:xaluca@xaluca.com"
              className="inline-flex items-center gap-2 border border-[#FDFBF7]/30 hover:border-[#FDFBF7] text-[#FDFBF7] px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
              data-testid="directions-footer-email"
            >
              xaluca@xaluca.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DirectionsPage;
